import { useEffect, useState } from "react";
import { adminListUsers, adminCreateUser, adminUpdateUser, adminDeleteUser } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield, ShieldCheck, KeyRound } from "lucide-react";

const ROLES = ["admin", "editor"];

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "editor" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // user id being password-reset

  const load = () => {
    setLoading(true);
    adminListUsers()
      .then(setUsers)
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 403) {
          toast.error("Admin role required to manage users.");
        } else {
          toast.error(typeof detail === "string" ? detail : "Couldn't load users.");
        }
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.email || form.password.length < 8) {
      toast.error("Please enter a valid email and a password of at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await adminCreateUser(form);
      toast.success("User added.");
      setForm({ email: "", name: "", password: "", role: "editor" });
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't create user.");
    } finally {
      setBusy(false);
    }
  };

  const setRole = async (u, role) => {
    try {
      await adminUpdateUser(u.id, { role });
      toast.success(`Role set to ${role}.`);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't change role.");
    }
  };

  const resetPassword = async (u) => {
    const pwd = window.prompt(`Set a new password for ${u.email} (min 8 chars):`);
    if (!pwd) return;
    if (pwd.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    try {
      await adminUpdateUser(u.id, { password: pwd });
      toast.success("Password updated.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't update password.");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? They will lose access immediately.`)) return;
    try {
      await adminDeleteUser(u.id);
      toast.success("User removed.");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't remove user.");
    }
  };

  return (
    <div className="px-6 lg:px-10 py-10 max-w-5xl">
      <div className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mb-2">Team</div>
      <h1 className="font-display text-4xl tracking-tight">Users & access</h1>
      <p className="text-[13px] text-[#A3A3A3] mt-3 max-w-2xl">
        Add team members. <strong>Admin</strong> can manage users and everything else.
        <strong className="ml-1">Editor</strong> can manage gallery, projects and inbox but cannot add users.
      </p>

      {/* Add user */}
      <form
        onSubmit={create}
        data-testid="add-user-form"
        className="mt-8 bg-[#0f0f0f] border border-[#1a1a1a] p-5"
      >
        <div className="flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mb-4">
          <UserPlus size={12} /> Add a user
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          <div>
            <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373]">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="hl-input"
              required
              data-testid="new-user-email"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373]">Display name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="hl-input"
              data-testid="new-user-name"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373]">Initial password</label>
            <input
              type="text"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="hl-input"
              required
              data-testid="new-user-password"
              placeholder="Min 8 characters"
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373]">Role</label>
            <div className="flex gap-2 mt-3">
              {ROLES.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, role: r }))}
                  data-testid={`new-user-role-${r}`}
                  className={`hl-chip ${form.role === r ? "active" : ""}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="new-user-submit"
          className="hl-btn-primary mt-6 disabled:opacity-60"
        >
          {busy ? "Adding…" : "Add user"}
        </button>
      </form>

      {/* List */}
      <div className="mt-10">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#737373] mb-3">
          Existing users
        </div>
        {loading ? (
          <div className="text-[#737373] text-xs tracking-[0.24em] uppercase py-8">Loading…</div>
        ) : (
          <div className="bg-[#0f0f0f] border border-[#1a1a1a]" data-testid="users-list">
            {users.map((u) => (
              <div
                key={u.id || u.email}
                data-testid={`user-row-${u.email}`}
                className="flex items-center justify-between gap-4 px-5 py-4 border-b border-[#1a1a1a] last:border-b-0 flex-wrap"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="w-8 h-8 rounded-full bg-[#1a1a1a] flex items-center justify-center text-[10px] tracking-[0.18em] text-[#A3A3A3] uppercase">
                    {(u.name || u.email).slice(0, 2)}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[13px] truncate">{u.email}</div>
                    <div className="text-[11px] text-[#737373] truncate">{u.name || "-"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {u.email === currentUser?.email && (
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#658CAE]">You</span>
                  )}
                  <div className="flex gap-1">
                    {ROLES.map((r) => (
                      <button
                        key={r}
                        onClick={() => setRole(u, r)}
                        disabled={u.role === r}
                        data-testid={`set-role-${u.email}-${r}`}
                        className={`text-[10px] tracking-[0.18em] uppercase px-2 py-1 border ${u.role === r ? "border-[#4A6D8C] text-white bg-[#4A6D8C]/20" : "border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#4A6D8C]"}`}
                      >
                        {r === "admin" ? <ShieldCheck size={11} className="inline mr-1" /> : <Shield size={11} className="inline mr-1" />}
                        {r}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => resetPassword(u)}
                    data-testid={`reset-${u.email}`}
                    className="text-[#A3A3A3] hover:text-white"
                    title="Reset password"
                  >
                    <KeyRound size={14} />
                  </button>
                  <button
                    onClick={() => remove(u)}
                    disabled={u.email === currentUser?.email}
                    data-testid={`delete-user-${u.email}`}
                    className="text-[#A3A3A3] hover:text-red-300 disabled:opacity-30 disabled:hover:text-[#A3A3A3]"
                    title="Remove user"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
